import { SERVER } from "./server";

/*
  Der Echtzeitkanal.

  PocketBase kann Änderungen von sich aus melden, statt darauf zu warten,
  dass jemand nachfragt. Das läuft über einen offenen Strom (SSE) unter
  `/api/realtime` und in zwei Zügen:

    1. Den Strom öffnen. Der Server schickt als erstes eine `clientId`.
    2. Mit dieser Kennung sagen, was einen interessiert — die Abonnements.

  Bewusst von Hand und ohne Bibliothek: Die Anwendung spricht überall mit
  `fetch` gegen PocketBase (siehe server.js), und das hier sind hundert
  Zeilen. Ein Paket dafür wäre die vierte Abhängigkeit.

  ---------------------------------------------------------------
  Was der Server dabei prüft
  ---------------------------------------------------------------

  Ein Abonnement hebelt die Zugriffsregeln NICHT aus. PocketBase prüft vor
  jedem Ereignis dieselbe Regel wie bei einer gewöhnlichen Abfrage. Wer die
  Schritte einer fremden Fraktion abonniert, bekommt schlicht nichts.
  Trotzdem wird genau das mit zwei Konten gegengeprüft — eine Regel, auf die
  man sich verlässt, ohne sie geprüft zu haben, ist keine.

  ---------------------------------------------------------------
  Abbrüche
  ---------------------------------------------------------------

  Ein Handy, das in den Ruhezustand geht, verliert den Strom. Das ist der
  Normalfall, nicht die Ausnahme. Deshalb wird wieder verbunden, und wer
  zuhört, erfährt es: Nach einem Abbruch muss nachgeladen werden, was
  während der Stille geschah — der Strom holt nichts nach.
*/

/* Nach einem Abbruch wird gewartet, bevor es neu versucht wird. */
const WARTEN_MS = [1000, 2000, 5000, 10000, 20000];

export function erzeugeKanal(token) {
  let strom = null;
  let clientId = null;
  let versuche = 0;
  let geschlossen = false;
  let uhr = null;

  /* Was abonniert ist, und wer darauf wartet. */
  const zuhoerer = new Map();
  /* Wer wissen will, dass die Verbindung neu aufgebaut wurde. */
  const beiNeuverbindung = new Set();

  function themen() {
    return Array.from(zuhoerer.keys());
  }

  async function meldeAbonnements() {
    if (!clientId) return;
    await fetch(`${SERVER}/api/realtime`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: token },
      body: JSON.stringify({ clientId, subscriptions: themen() }),
    });
  }

  function verbinde() {
    if (geschlossen) return;

    /*
      Der Strom selbst wird OHNE Anmeldung geöffnet — ein EventSource kann
      keine Kopfzeilen mitschicken. Angemeldet wird beim Melden der
      Abonnements gleich darunter, und PocketBase merkt sich das Konto zu
      dieser `clientId`. Erst ab da fließt überhaupt etwas.
    */
    strom = new EventSource(`${SERVER}/api/realtime`);

    strom.addEventListener("PB_CONNECT", async (e) => {
      try {
        const daten = JSON.parse(e.data);
        clientId = daten.clientId;
        const warEinNeuanfang = versuche > 0;
        versuche = 0;
        await meldeAbonnements();
        /*
          Nach einem Abbruch fehlt, was in der Zwischenzeit geschah. Der
          Strom holt es nicht nach — also müssen die Zuhörer selbst
          nachladen. Ohne diesen Anstoß fehlte am Ende Text im Dokument,
          und niemand wüsste warum.
        */
        if (warEinNeuanfang) beiNeuverbindung.forEach((f) => f());
      } catch {
        /* Unverständliche Begrüßung — dann eben neu verbinden. */
      }
    });

    for (const thema of themen()) lausche(thema);

    /*
      Hier NICHT selbst neu verbinden, solange der Browser es noch tut.

      Ein EventSource baut von sich aus wieder auf; `readyState` sagt, ob es
      das gerade versucht (CONNECTING) oder aufgegeben hat (CLOSED). Wer bei
      jedem Zucken zusätzlich eine neue Verbindung öffnet, hat nach einer
      Weile fünf offene Ströme, von denen vier keine Abonnements tragen —
      und wundert sich, warum Änderungen ankommen und dann wieder nicht.
      Genau das ist beim ersten Anlauf passiert.

      Beim Wiederkommen schickt der Server ohnehin eine neue `clientId`, und
      die Abonnements werden neu gemeldet.
    */
    strom.onerror = () => {
      if (geschlossen) return;
      if (strom && strom.readyState !== EventSource.CLOSED) {
        /* Der Browser versucht es selbst. Nur die Kennung ist hinfällig. */
        clientId = null;
        versuche += 1;
        return;
      }

      strom = null;
      clientId = null;
      const wartezeit = WARTEN_MS[Math.min(versuche, WARTEN_MS.length - 1)];
      versuche += 1;
      uhr = setTimeout(verbinde, wartezeit);
    };
  }

  function lausche(thema) {
    if (!strom) return;
    strom.addEventListener(thema, (e) => {
      let daten;
      try {
        daten = JSON.parse(e.data);
      } catch {
        return;
      }
      const menge = zuhoerer.get(thema);
      if (menge) menge.forEach((f) => f(daten));
    });
  }

  return {
    /*
      `thema` ist eine PocketBase-Kennung, etwa „dokumentschritte" für die
      ganze Sammlung. Zurück kommt die Funktion zum Abbestellen.
    */
    async abonniere(thema, aufEreignis) {
      let menge = zuhoerer.get(thema);
      const istNeu = !menge;
      if (istNeu) {
        menge = new Set();
        zuhoerer.set(thema, menge);
      }
      menge.add(aufEreignis);

      if (!strom) verbinde();
      else if (istNeu) {
        lausche(thema);
        await meldeAbonnements();
      }

      return () => {
        const m = zuhoerer.get(thema);
        if (!m) return;
        m.delete(aufEreignis);
        if (m.size === 0) {
          zuhoerer.delete(thema);
          meldeAbonnements();
        }
      };
    },

    beiWiederverbindung(f) {
      beiNeuverbindung.add(f);
      return () => beiNeuverbindung.delete(f);
    },

    schliesse() {
      geschlossen = true;
      if (uhr) clearTimeout(uhr);
      if (strom) strom.close();
      strom = null;
      clientId = null;
      zuhoerer.clear();
      beiNeuverbindung.clear();
    },
  };
}
